import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { Icon } from '../../components/icons';
import { ErrorNote, Loading, cx } from '../../components/ui';
import { api, type CompanyDetail, type Plan } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime, formatMoney, monthName } from '../../lib/format';
import { AButton, AField, AINPUT, ATD, ATH, ATR, AdminShell, Panel, Tile, UsageBars } from './shared';

const rupees = (paise: number) => (paise / 100).toString();
const paise = (rupees: string) => Math.round((Number(rupees) || 0) * 100);

/** One company as the platform sees it: plan, month, invoices and people. Never its candidates. */
export function AdminCompanyPage() {
  const { user } = useAuth();
  const { id } = useParams() as { id: string };
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'company', id], queryFn: () => api.get<CompanyDetail>(`/api/orgs/${id}`), enabled: !!user?.platformAdmin });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  if (!user?.platformAdmin) return <Navigate to="/" replace />;
  const c = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin'] });

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
      await qc.invalidateQueries({ queryKey: ['companies'] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminShell
      eyebrow={
        <Link to="/admin/companies" className="inline-flex items-center gap-1 hover:underline">
          <Icon name="arrowLeft" className="h-3.5 w-3.5" /> Companies
        </Link>
      }
      title={c?.name ?? '…'}
      subtitle={c ? `${c.team.length} people · ${c.jobs} jobs · ${c.candidates} candidates · since ${formatDateTime(c.createdAt).split(',')[0]}${c.active ? '' : ' · suspended'}` : undefined}
      actions={
        c &&
        c.id !== user.orgId && (
          <AButton
            danger={c.active}
            loading={busy === 'active'}
            onClick={() => {
              if (c.active && !window.confirm(`Suspend ${c.name}? Its people are signed out at once and cannot sign in until you restore it. Nothing is deleted.`)) return;
              void act('active', () => api.patch(`/api/orgs/${c.id}`, { active: !c.active }));
            }}
          >
            {c.active ? 'Suspend company' : 'Restore company'}
          </AButton>
        )
      }
    >
      <ErrorNote error={q.error ?? error} className="mb-5" />
      {q.isLoading && <Loading />}
      {c && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Tile label={`Interviews, ${monthName(c.period.year, c.period.month)}`} value={c.month.interviews} note={c.plan.includedInterviews ? `${c.plan.includedInterviews} included${c.month.extraInterviews ? `, ${c.month.extraInterviews} extra` : ''}` : 'pay as you go'} tone={c.month.extraInterviews ? 'warn' : 'default'} />
            <Tile label="Minutes" value={c.month.minutes} note="AI interviews and assessments" />
            <Tile label="This month so far" value={formatMoney(c.month.total)} tone="brand" note={`${formatMoney(c.month.monthlyFee)} plan + ${formatMoney(c.month.usageFee)} usage`} />
            <Tile label="Outstanding" value={formatMoney(c.invoices.filter((i) => i.status === 'ISSUED').reduce((n, i) => n + i.total, 0))} tone={c.invoices.some((i) => i.status === 'ISSUED') ? 'warn' : 'good'} note={`${c.invoices.filter((i) => i.status === 'ISSUED').length} unpaid invoice(s)`} />
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <PlanPanel company={c} busy={busy === 'plan'} onSave={(plan, billingEmail, billingNotes) => act('plan', () => api.patch(`/api/orgs/${c.id}`, { ...plan, billingEmail, billingNotes }))} />
            <Panel title="Usage, last six months" description="Interviews held each month; the dashed line is the plan's allowance.">
              <UsageBars history={c.history} included={c.plan.includedInterviews} />
            </Panel>
          </div>

          <InvoicesPanel company={c} busy={busy} act={act} />
          <TeamPanel company={c} busy={busy} act={act} />
        </div>
      )}
    </AdminShell>
  );
}

function PlanPanel({ company, busy, onSave }: { company: CompanyDetail; busy: boolean; onSave: (plan: Plan, billingEmail: string | null, billingNotes: string | null) => void }) {
  const [f, setF] = useState({ planName: company.plan.planName, included: String(company.plan.includedInterviews), price: rupees(company.plan.pricePerInterview), fee: rupees(company.plan.monthlyFee), email: company.billingEmail ?? '', notes: company.billingNotes ?? '' });
  useEffect(() => {
    setF({ planName: company.plan.planName, included: String(company.plan.includedInterviews), price: rupees(company.plan.pricePerInterview), fee: rupees(company.plan.monthlyFee), email: company.billingEmail ?? '', notes: company.billingNotes ?? '' });
  }, [company]);
  return (
    <Panel title="Plan" description="What this company pays. Changes apply to the next invoice raised.">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ planName: f.planName.trim(), includedInterviews: Math.max(0, Math.round(Number(f.included) || 0)), pricePerInterview: paise(f.price), monthlyFee: paise(f.fee) }, f.email.trim() || null, f.notes.trim() || null);
        }}
      >
        <AField label="Plan name">
          <input className={AINPUT} required value={f.planName} onChange={(e) => setF({ ...f, planName: e.target.value })} />
        </AField>
        <AField label="Monthly fee (₹)">
          <input className={AINPUT} inputMode="decimal" value={f.fee} onChange={(e) => setF({ ...f, fee: e.target.value })} />
        </AField>
        <AField label="Interviews included a month" hint="AI interviews and skills assessments. 0 for pay as you go.">
          <input className={AINPUT} inputMode="numeric" value={f.included} onChange={(e) => setF({ ...f, included: e.target.value })} />
        </AField>
        <AField label="Price per extra interview (₹)">
          <input className={AINPUT} inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
        </AField>
        <AField label="Billing email" hint="Where invoices go. The company can change it too.">
          <input className={AINPUT} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </AField>
        <AField label="Notes" hint="What was agreed. Platform eyes only.">
          <input className={AINPUT} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </AField>
        <div className="flex justify-end sm:col-span-2">
          <AButton primary type="submit" loading={busy}>
            Save plan
          </AButton>
        </div>
      </form>
    </Panel>
  );
}

function monthOptions(now = new Date()): Array<{ year: number; month: number }> {
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
  });
}

function InvoicesPanel({ company, busy, act }: { company: CompanyDetail; busy: string | null; act: (key: string, fn: () => Promise<unknown>) => Promise<void> }) {
  const options = monthOptions();
  const [period, setPeriod] = useState(`${options[1]?.year}-${options[1]?.month}`);
  const [note, setNote] = useState('');
  const STATUS = { ISSUED: 'bg-[#fbbf24]/15 text-[#fbbf24]', PAID: 'bg-[#4ade80]/15 text-[#4ade80]', VOID: 'bg-white/10 text-white/50' };
  return (
    <Panel
      title="Invoices"
      description="Raise one for a month from its usage and the plan as it stands; mark it paid when the money arrives."
      actions={
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const [year, month] = period.split('-').map(Number);
            void act('invoice', () => api.post(`/api/orgs/${company.id}/invoices`, { year, month, ...(note.trim() ? { note: note.trim() } : {}) })).then(() => setNote(''));
          }}
        >
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className={cx(AINPUT, 'h-9 w-auto')} aria-label="Month to invoice">
            {options.map((o) => (
              <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
                {monthName(o.year, o.month)}
              </option>
            ))}
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={cx(AINPUT, 'h-9 w-44')} />
          <AButton primary type="submit" loading={busy === 'invoice'}>
            Raise invoice
          </AButton>
        </form>
      }
      padding={false}
    >
      {company.invoices.length === 0 ? (
        <p className="px-5 py-6 text-sm text-white/50">No invoices yet.</p>
      ) : (
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr>
                <th className={ATH}>Number</th>
                <th className={ATH}>Month</th>
                <th className={cx(ATH, 'text-right')}>Interviews</th>
                <th className={cx(ATH, 'text-right')}>Plan</th>
                <th className={cx(ATH, 'text-right')}>Usage</th>
                <th className={cx(ATH, 'text-right')}>Total</th>
                <th className={ATH}>Status</th>
                <th className={ATH} />
              </tr>
            </thead>
            <tbody>
              {company.invoices.map((i) => {
                const d = new Date(i.periodStart);
                return (
                  <tr key={i.id} className={ATR}>
                    <td className={cx(ATD, 'font-mono text-xs')}>
                      {i.number}
                      {i.note && <div className="mt-0.5 text-white/45">{i.note}</div>}
                    </td>
                    <td className={ATD}>{monthName(d.getUTCFullYear(), d.getUTCMonth() + 1)}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{i.interviews}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{formatMoney(i.monthlyFee)}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular')}>{formatMoney(i.usageFee)}</td>
                    <td className={cx(ATD, 'text-right font-mono tabular font-semibold')}>{formatMoney(i.total)}</td>
                    <td className={ATD}>
                      <span className={cx('rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider', STATUS[i.status])}>{i.status}</span>
                      {i.paidAt && <div className="mt-0.5 text-[11px] text-white/45">paid {formatDateTime(i.paidAt)}</div>}
                    </td>
                    <td className={cx(ATD, 'text-right')}>
                      <span className="inline-flex gap-1">
                        {i.status === 'ISSUED' && (
                          <AButton primary loading={busy === `inv-${i.id}`} onClick={() => void act(`inv-${i.id}`, () => api.patch(`/api/invoices/${i.id}`, { status: 'PAID' }))} className="!h-8">
                            Mark paid
                          </AButton>
                        )}
                        {i.status !== 'VOID' && (
                          <AButton danger loading={busy === `void-${i.id}`} onClick={() => window.confirm(`Void ${i.number}?`) && void act(`void-${i.id}`, () => api.patch(`/api/invoices/${i.id}`, { status: 'VOID' }))} className="!h-8">
                            Void
                          </AButton>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function TeamPanel({ company, busy, act }: { company: CompanyDetail; busy: string | null; act: (key: string, fn: () => Promise<unknown>) => Promise<void> }) {
  const [f, setF] = useState({ name: '', email: '', password: '', role: 'ADMIN' as 'ADMIN' | 'RECRUITER' });
  return (
    <Panel title="People" description="Accounts in this company. Add one for the person who needs the system; they add their colleagues under Settings." padding={false}>
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr>
              <th className={ATH}>Person</th>
              <th className={ATH}>Role</th>
              <th className={ATH}>Access</th>
              <th className={ATH}>Since</th>
            </tr>
          </thead>
          <tbody>
            {company.team.map((u) => (
              <tr key={u.id} className={ATR}>
                <td className={ATD}>
                  <div className={cx('font-semibold', !u.active && 'text-white/40 line-through')}>{u.name}</div>
                  <div className="text-xs text-white/45">{u.email}</div>
                </td>
                <td className={cx(ATD, 'font-mono text-xs')}>
                  {u.role.toLowerCase()}
                  {u.platformAdmin && <span className="ml-2 rounded-full bg-[#d6217f]/20 px-1.5 py-px text-[10px] text-[#f7a6cf]">platform</span>}
                </td>
                <td className={cx(ATD, 'font-mono text-xs')}>{u.active ? <span className="text-[#4ade80]">on</span> : <span className="text-[#fbbf24]">switched off</span>}</td>
                <td className={cx(ATD, 'text-xs text-white/55')}>{formatDateTime(u.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form
        className="grid gap-3 border-t border-white/10 p-5 md:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          void act('user', () => api.post(`/api/orgs/${company.id}/users`, f)).then(() => setF({ name: '', email: '', password: '', role: 'ADMIN' }));
        }}
      >
        <p className="text-sm font-semibold md:col-span-5">Add an account</p>
        <AField label="Name">
          <input className={AINPUT} required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </AField>
        <AField label="Email">
          <input className={AINPUT} required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </AField>
        <AField label="Temporary password">
          <input className={AINPUT} required minLength={10} type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        </AField>
        <AField label="Role">
          <select className={AINPUT} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as 'ADMIN' | 'RECRUITER' })}>
            <option value="ADMIN">Admin</option>
            <option value="RECRUITER">Recruiter</option>
          </select>
        </AField>
        <div className="flex items-end">
          <AButton primary type="submit" loading={busy === 'user'} className="w-full">
            Add account
          </AButton>
        </div>
      </form>
    </Panel>
  );
}
